import { useEffect, useMemo, useRef, useState } from 'react';

import type { ValidationState } from '@ui-component-library/types/status';

import { Tooltip } from '@ui-component-library/components/Tooltip';

import { formatInputValue, sanitizeInputValue } from '@ui-component-library/utils/input';

import { useDebounce } from '@ui-component-library/hooks/useDebounce';

import { Icon } from '../Icon';
import { SuffixIcon } from '../Input/SuffixIcon/SuffixIcon';
import { mapInputType } from './utils';

import type { InputProps } from './Input.types';

import { S } from './Input.styles';

export const Input = ({
  variant = 'default',
  type,
  value = '',
  disabled = false,
  required = false,
  prefix,
  suffix,

  label,
  description,
  placeholder,

  idleText,
  errorText,
  successText,

  validation,
  name,
  onChange,
  isValid,
  tabIndex = 0
}: InputProps) => {
  const [inputValue, setInputValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [validationState, setValidationState] = useState<ValidationState>('idle');
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedValue = useDebounce(inputValue, 1000);
  const effectiveValue = validation ? debouncedValue : inputValue;

  const helperTexts = {
    idle: idleText,
    error: errorText,
    success: successText
  };

  const isTyping = validation ? inputValue !== effectiveValue : false;
  const isFluid = variant === 'fluid';
  const [isLabelFloating, setIsLabelFloating] = useState(!!inputValue);

  const isValueValid = useMemo(() => {
    if (!effectiveValue) {
      return required ? false : true;
    }
    return validation ? validation(effectiveValue) : true;
  }, [effectiveValue, validation, required]);

  // Update internal state when external value prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!validation) {
      return;
    }

    if (isValid !== undefined && !effectiveValue) {
      setValidationState(isValid ? 'success' : 'error');
      return;
    }

    if (effectiveValue) {
      setValidationState(isValueValid ? 'success' : 'error');
    }
  }, [effectiveValue, isValueValid, isValid, validation]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const displayValue = formatInputValue(newValue, type);

    setInputValue(displayValue);

    if (!validation) {
      const cleanValue = sanitizeInputValue(displayValue, type);
      onChange?.(cleanValue, true, name);
    }
  };

  useEffect(() => {
    if (!validation) {
      return;
    }

    if (inputValue !== value) {
      const cleanValue = sanitizeInputValue(effectiveValue, type);
      onChange?.(cleanValue, isValueValid, name);
    }
  }, [effectiveValue, validation, inputValue, value, onChange, isValueValid, name, type]);

  const handleFocus = () => {
    setIsFocused(true);
    if (isFluid) {
      setIsLabelFloating(true);

      if (!disabled && inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (isFluid && !inputValue) {
      setIsLabelFloating(false);
    }

    if (!validation) {
      return;
    }

    if (required && !inputValue) {
      setValidationState('error');
      return;
    }

    if (inputValue) {
      setValidationState(isValueValid ? 'success' : 'error');
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setInputValue('');
    setValidationState('idle');

    // Immediately notify of clear action - don't wait for debounce
    onChange('', false, name);

    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  };

  const shouldShowHelperText = () => {
    if (disabled || !helperTexts[validationState]) {
      return false;
    }

    return validation && (validationState === 'error' || isFocused);
  };

  return (
    <S.Wrapper variant={variant} disabled={disabled || false}>
      {!isFluid && (
        <S.TopSection>
          <S.Label htmlFor={name}>
            {label}
            {required && <>*</>}
          </S.Label>
          {!disabled && <S.Description>{description}</S.Description>}
        </S.TopSection>
      )}
      <S.InputContainer $state={disabled ? 'disabled' : validationState} onClick={handleFocus}>
        {prefix && <S.Prefix $isFocused={isFocused}>{prefix}</S.Prefix>}
        {isFluid && (
          <S.FluidLabel $isFloating={isLabelFloating} htmlFor={name}>
            <Tooltip content={description} aria-label={`More information about ${label}`}>
              {label}
              {required && <>*</>}

              <S.FluidLabelIcon>
                <Icon name="InfoFilled" size="small" color="charcoal-500" aria-hidden="true" />
              </S.FluidLabelIcon>
            </Tooltip>
          </S.FluidLabel>
        )}
        <S.Input
          ref={inputRef}
          type={mapInputType(type)}
          value={inputValue || ''}
          disabled={disabled}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={isFluid ? '' : placeholder}
          $isFluid={isFluid}
          aria-describedby={description}
          tabIndex={tabIndex}
        />
        <S.SuffixWrapper>
          {(inputValue || !disabled) && (
            <SuffixIcon
              isTyping={isTyping}
              isFocused={isFocused}
              validationState={validationState}
              handleClear={handleClear}
            />
          )}
          {suffix && <S.Suffix>{suffix}</S.Suffix>}
        </S.SuffixWrapper>
      </S.InputContainer>
      <S.BottomSection>
        {shouldShowHelperText() && (
          <S.HelperText $validationState={validationState}>
            {helperTexts[validationState]}
          </S.HelperText>
        )}
      </S.BottomSection>
    </S.Wrapper>
  );
};
